import { useDeferredValue, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { useNavigate, useParams } from 'react-router-dom';

import { PageHeader } from '@/components/page-header';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ErrorNotice } from '@/components/ui/error-notice';
import { Label } from '@/components/ui/label';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { RouteBreadcrumb } from '@/components/route-breadcrumb';
import { useConfirmDialog } from '@/hooks/useConfirmDialog';

import { useAppDispatch, useAppSelector } from '@/store';
import { selectSchemaByPkSk, selectPublishedSchemas } from '@/features/property-schemas/schemasSlice';
import { fetchSchemaById, fetchSchemas } from '@/features/property-schemas/schemasThunks';

import { selectPropertyTemplateById } from '@/features/property-templates/propertyTemplatesSlice';
import {
  cloneTemplate,
  createPropertyTemplate,
  deprecateTemplate,
  fetchTemplateById,
  updatePropertyTemplate,
} from '@/features/property-templates/propertyTemplatesThunks';

import type { AttributeNode } from '@/types/schemas';
import type { JsonRecord, JsonSchemaObject, JsonValue } from '@/domain/entities/property-schema';
import type { PropertyTemplate } from '@/domain/entities/preperty-template';

import {
  buildIncludedFromData,
  collectDescendants,
  flattenTree,
  getAtPath,
  nowUnixSeconds,
  sanitizeTemplateDataToSchema,
  schemaToBlocks,
  setAtPath,
  unsetAtPath,
  validateTemplateImportPayload,
} from './utils';

import { SchemaFieldTree } from './components/SchemaFieldTree';
import type { FlatField, SchemaSwitchLog } from './types';
import { TemplateMetaCard } from './components/TemplateMetaCard';
import { TemplateInspectorCard } from './components/TemplateInspectorCard';
import { TemplatePreviewCard } from './components/TemplatePreviewCard';
import { SchemaIssuesAlert, type IssuesLog } from './components/SchemaIssuesAlert';
import {
  hasSchemaValidationIssues,
  validateTemplateDataAgainstSchema,
  type SchemaValidationLog,
} from '@/schemas/validators';
import { selectActiveOrganizationId } from '@/store/selectors/authorizationSelectors';
import { usePermissions } from '@/hooks/usePermissions';
import { Permissions } from '@/lib/permissions';

type ApiErrorState = {
  title: string;
  message: string;
};

function issuesFromSchemaSwitchLog(log: SchemaSwitchLog): IssuesLog {
  return {
    removedPaths: log.removedPaths ?? [],
    mismatchedPaths: log.mismatchedPaths ?? [],
  };
}

function issuesFromSchemaValidationLog(log: SchemaValidationLog): IssuesLog {
  return {
    removedPaths: [], // validation isn't "removal"
    mismatchedPaths: [
      ...log.missingRequired.map((p) => `${p} (missing required)`),
      ...log.typeMismatches.map((m) => `${m.path} (expected ${m.expected}, got ${m.actual})`),
      ...log.enumMismatches.map((m) => `${m.path} (must be one of: ${m.allowed.join(', ')}, got ${m.actual})`),
    ],
  };
}

function formatTemplateValidationErrors(log: SchemaValidationLog): string[] {
  return [
    ...log.missingRequired.map((path) => `${path}: missing required value.`),
    ...log.typeMismatches.map(({ path, expected, actual }) => `${path}: expected ${expected}, got ${actual}.`),
    ...log.enumMismatches.map(
      ({ path, allowed, actual }) => `${path}: must be one of ${allowed.join(', ')}, got ${actual}.`,
    ),
  ];
}

/** ------------------------------------------------------------------ */
/** Main Page                                                           */
/** ------------------------------------------------------------------ */

export default function AddTemplatePage() {
  /** ------------------------------------------------------------------ */
  /** Navigation + Routing                                                */
  /** ------------------------------------------------------------------ */
  const navigate = useNavigate();
  const { templateId } = useParams<{ templateId: string }>();
  const isEditMode = !!templateId;

  /** ------------------------------------------------------------------ */
  /** Redux: dispatch + selectors                                          */
  /** ------------------------------------------------------------------ */
  const dispatch = useAppDispatch();
  const confirm = useConfirmDialog();
  const { hasPermission, isSuperAdmin } = usePermissions();
  const canCreateTemplates = hasPermission(Permissions.TemplatesCreate);
  const canUpdateTemplates = hasPermission(Permissions.TemplatesUpdate);

  const orgID = useAppSelector(selectActiveOrganizationId);

  // Template being edited (must already exist in store; we lazily fetch drafts if needed)
  const storeTemplate = useAppSelector((s) => selectPropertyTemplateById(s, templateId));

  // Schemas: only published are selectable
  const publishedSchemas = useAppSelector(selectPublishedSchemas);
  const hasFetchedPublished = useAppSelector((s) => s.propertySchemas.hasFetchedByStatus.published);
  const isListingPublished = useAppSelector((s) => s.propertySchemas.isListingByStatus.published);
  const publishedSchemasError = useAppSelector((s) => s.propertySchemas.errorByStatus.published);
  const isLoadingSelectedSchema = useAppSelector((s) => s.propertySchemas.isLoadingOne);
  const isSavingTemplate = useAppSelector((s) => s.propertyTemplates.isSaving);
  const isLoadingTemplate = useAppSelector((s) => s.propertyTemplates.isLoadingOne);
  const isCloningTemplate = useAppSelector((s) => s.propertyTemplates.isCloning);
  const isDeprecatingTemplate = useAppSelector((s) => s.propertyTemplates.isDeprecating);
  const isLoadingPublishedSchemas = !hasFetchedPublished && isListingPublished;

  /** ------------------------------------------------------------------ */
  /** Local state: template draft + UI state                               */
  /** ------------------------------------------------------------------ */

  /**
   * Schema selection key: `${schemaId}::${version}`
   * - In edit mode: hydrated from `storeTemplate.schemaRef`
   * - In create mode: user selects from dropdown
   */
  const [selectedSchemaKey, setSelectedSchemaKey] = useState<string>('');

  /**
   * Warning box shown when switching schema or validating:
   * - removedPaths: fields present in template but not allowed in schema
   * - mismatchedPaths: fields that exist but have incompatible structure/type
   */
  const [schemaSwitchLog, setSchemaSwitchLog] = useState<SchemaSwitchLog | null>(null);

  /**
   * Template validation against chosen schema on submit
   */
  const [templateValidationLog, setTemplateValidationLog] = useState<SchemaValidationLog | null>(null);

  /**
   * Tracks last schema key so we can detect schema switches and sanitize once.
   */
  const [lastSchemaKey, setLastSchemaKey] = useState<string>('');

  // Track if user has tried to save (for validation UI)
  const [didTrySave, setDidTrySave] = useState(false);
  const [hasAttemptedTemplateLoad, setHasAttemptedTemplateLoad] = useState(false);
  const [templateApiError, setTemplateApiError] = useState<ApiErrorState | null>(null);
  const [schemaApiError, setSchemaApiError] = useState<ApiErrorState | null>(null);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [importPayload, setImportPayload] = useState('');
  const [importError, setImportError] = useState<string | null>(null);

  /**
   * `tpl` is the local editable draft. We keep it as source of truth for the builder UI.
   * The API payload comes from this object.
   */
  const [tpl, setTpl] = useState<PropertyTemplate>(() => {
    const ts = nowUnixSeconds();
    return {
      id: 'TEMPLATE#new',
      scope: isSuperAdmin ? 'system' : 'organization',
      orgID: orgID || '',
      audience: 'business',
      status: 'draft',
      schemaRef: { schemaId: '', version: '' },
      title: '',
      description: '',
      data: {},
      createdAt: ts,
      updatedAt: ts,
      typeName: 'PropertyTemplate',
    };
  });

  /**
   * Tree selection + included toggles.
   * - `included`: controls the toggle switch states in schema tree.
   * - `selectedPath`: which node is currently inspected on the right.
   */
  const [included, setIncluded] = useState<Record<string, boolean>>({});
  const [selectedPath, setSelectedPath] = useState<string | null>(null);

  /**
   * Preview helpers
   */
  const [copied, setCopied] = useState(false);
  const [isHydratingSchemaTree, setIsHydratingSchemaTree] = useState(false);

  /**
   * Expand/collapse per schema path
   */
  const [schemaOpenByPath, setSchemaOpenByPath] = useState<Record<string, boolean>>({});
  const [schemaBlocks, setSchemaBlocks] = useState<AttributeNode[]>([]);
  const [flatFields, setFlatFields] = useState<FlatField[]>([]);
  const [hydratedSchemaKey, setHydratedSchemaKey] = useState('');
  const templateStatus = storeTemplate?.status ?? tpl.status;
  const readOnly =
    isEditMode && (!canUpdateTemplates || templateStatus === 'published' || templateStatus === 'deprecated');
  const canShowSaveDraft = !readOnly && (isEditMode ? canUpdateTemplates : canCreateTemplates);
  const hasUnsavedLocalChanges = useMemo(() => {
    if (!isEditMode || !storeTemplate || readOnly) return false;

    return (
      tpl.scope !== storeTemplate.scope ||
      tpl.orgID !== storeTemplate.orgID ||
      tpl.audience !== storeTemplate.audience ||
      tpl.status !== storeTemplate.status ||
      tpl.title !== (storeTemplate.title ?? '') ||
      (tpl.description ?? '') !== (storeTemplate.description ?? '') ||
      tpl.schemaRef.schemaId !== storeTemplate.schemaRef.schemaId ||
      tpl.schemaRef.version !== storeTemplate.schemaRef.version ||
      JSON.stringify(tpl.data ?? {}) !== JSON.stringify(storeTemplate.data ?? {})
    );
  }, [
    isEditMode,
    readOnly,
    storeTemplate,
    tpl.scope,
    tpl.audience,
    tpl.data,
    tpl.description,
    tpl.orgID,
    tpl.schemaRef,
    tpl.status,
    tpl.title,
  ]);
  const canSaveDraft = canShowSaveDraft && (!isEditMode || hasUnsavedLocalChanges);

  /** ------------------------------------------------------------------ */
  /** Derived: selected schema + blocks + flattened tree                   */
  /** ------------------------------------------------------------------ */

  const selectedSchemaRef = useMemo(() => {
    const [id, version] = selectedSchemaKey.split('::');
    if (!id || !version) return null;
    return { id, version };
  }, [selectedSchemaKey]);

  const selectedSchema = useAppSelector((state) =>
    selectedSchemaRef ? selectSchemaByPkSk(state, selectedSchemaRef.id, selectedSchemaRef.version) : null,
  );

  const schemaOptions = useMemo(() => {
    const map = new Map<string, (typeof publishedSchemas)[number]>();
    for (const schema of publishedSchemas) {
      map.set(`${schema.id}::${schema.version}`, schema);
    }
    if (selectedSchema) {
      map.set(`${selectedSchema.id}::${selectedSchema.version}`, selectedSchema);
    }
    return [...map.values()];
  }, [publishedSchemas, selectedSchema]);

  /**
   * Set of schema paths that are required.
   * Required nodes are always forced ON in included map.
   */
  const requiredPaths = useMemo(() => {
    const set = new Set<string>();
    for (const f of flatFields) {
      if (f.node.required) set.add(f.path);
    }
    return set;
  }, [flatFields]);

  /**
   * Utility helper for toggling ON: we seed object/array containers if missing.
   */
  const getNodeByPath = (path: string) => flatFields.find((f) => f.path === path)?.node ?? null;

  /**
   * Schema lock rules:
   * - draft templates: schema can be switched (even in edit mode)
   * - published/deprecated: schema locked
   */
  const schemaLocked = useMemo(() => {
    if (tpl.status === 'draft') return false;
    return true;
  }, [tpl.status]);

  /**
   * Selected inspector field + value.
   */
  const selectedField = useMemo(() => {
    if (!selectedPath) return null;
    return flatFields.find((f) => f.path === selectedPath) ?? null;
  }, [flatFields, selectedPath]);

  const selectedValue = useMemo<unknown | undefined>(() => {
    if (!selectedPath) return undefined;
    return getAtPath(tpl.data, selectedPath);
  }, [tpl.data, selectedPath]);
  const breadcrumbTemplateLabel = useMemo(() => {
    if (!isEditMode) return 'New Template';
    return tpl.title?.trim() || storeTemplate?.title?.trim() || 'Template Details';
  }, [isEditMode, storeTemplate?.title, tpl.title]);
  const templateBreadcrumb = useMemo(
    () => (
      <RouteBreadcrumb
        items={[{ label: 'Templates', to: '/templates' }, { label: breadcrumbTemplateLabel }]}
        separator=">"
      />
    ),
    [breadcrumbTemplateLabel],
  );

  /** ------------------------------------------------------------------ */
  /** Effects: data fetching                                               */
  /** ------------------------------------------------------------------ */

  // Ensure published schemas exist for dropdown
  useEffect(() => {
    if (!hasFetchedPublished) {
      dispatch(fetchSchemas({ status: 'published' }));
    }
  }, [dispatch, hasFetchedPublished]);

  useEffect(() => {
    if (!selectedSchemaRef || selectedSchema) return;
    setSchemaApiError(null);
    void dispatch(fetchSchemaById({ id: selectedSchemaRef.id, version: selectedSchemaRef.version }))
      .unwrap()
      .catch((error) => {
        const message =
          typeof error === 'string'
            ? error
            : (error as Error)?.message
              ? String((error as Error).message)
              : 'Failed to load schema';
        setSchemaApiError({ title: 'Failed to Load Schema', message });
      });
  }, [dispatch, selectedSchema, selectedSchemaRef]);

  useEffect(() => {
    if (!selectedSchemaRef || selectedSchema) {
      setSchemaApiError(null);
    }
  }, [selectedSchema, selectedSchemaRef]);

  useEffect(() => {
    if (!selectedSchema) {
      setSchemaBlocks([]);
      setFlatFields([]);
      setHydratedSchemaKey('');
      setIsHydratingSchemaTree(false);
      return;
    }

    const nextKey = `${selectedSchema.id}::${selectedSchema.version}`;
    const root = selectedSchema.jsonSchema as JsonSchemaObject | undefined;

    setIsHydratingSchemaTree(true);

    const frame = window.requestAnimationFrame(() => {
      const blocks = root?.type ? schemaToBlocks(root) : [];
      setSchemaBlocks(blocks);
      setFlatFields(flattenTree(blocks));
      setHydratedSchemaKey(nextKey);
      setIsHydratingSchemaTree(false);
    });

    return () => {
      window.cancelAnimationFrame(frame);
      setIsHydratingSchemaTree(false);
    };
  }, [selectedSchema]);

  // In edit mode, hydrate directly from the route id so refresh/deep links work for any status.
  useEffect(() => {
    if (!isEditMode) return;
    if (!templateId) return;
    if (storeTemplate) {
      setHasAttemptedTemplateLoad(true);
      setTemplateApiError(null);
      return;
    }

    let cancelled = false;
    setHasAttemptedTemplateLoad(false);
    setTemplateApiError(null);

    dispatch(fetchTemplateById({ id: templateId }))
      .unwrap()
      .catch((error) => {
        if (cancelled) return;

        const message =
          typeof error === 'string'
            ? error
            : (error as Error)?.message
              ? String((error as Error).message)
              : 'Failed to load template';
        setTemplateApiError({ title: 'Failed to Load Template', message });
      })
      .finally(() => {
        if (!cancelled) {
          setHasAttemptedTemplateLoad(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [dispatch, isEditMode, storeTemplate, templateId]);

  /** ------------------------------------------------------------------ */
  /** Effects: hydrate edit mode                                           */
  /** ------------------------------------------------------------------ */

  /**
   * Hydrate local draft (`tpl`) from Redux template record (edit mode only).
   * Also pins schema selection and builds initial included map.
   */
  useEffect(() => {
    if (!isEditMode) return;
    if (!storeTemplate) return;

    // 1) Hydrate tpl from stored template
    setTpl({
      id: storeTemplate.id,
      scope: storeTemplate.scope,
      orgID: storeTemplate.orgID,
      audience: storeTemplate.audience,
      status: storeTemplate.status,
      schemaRef: storeTemplate.schemaRef,
      title: storeTemplate.title ?? '',
      description: storeTemplate.description ?? '',
      data: (storeTemplate.data ?? {}) as JsonRecord,
      createdAt: storeTemplate.createdAt ?? nowUnixSeconds(),
      updatedAt: storeTemplate.updatedAt ?? nowUnixSeconds(),
      typeName: 'PropertyTemplate',
    });

    // 2) Pin schema selection
    setSelectedSchemaKey(`${storeTemplate.schemaRef.schemaId}::${storeTemplate.schemaRef.version}`);

    // 3) Included map from data (+ force required ON)
    const nextIncluded = buildIncludedFromData(storeTemplate.data ?? {}, null, {});
    for (const p of requiredPaths) nextIncluded[p] = true;
    setIncluded(nextIncluded);

    // 4) Clear inspector selection
    setSelectedPath(null);
  }, [isEditMode, requiredPaths, storeTemplate]);

  /** ------------------------------------------------------------------ */
  /** Effects: schema tree UI                                              */
  /** ------------------------------------------------------------------ */

  // Reset tree open/closed state on schema changes or edit/create switch
  useEffect(() => {
    setSchemaOpenByPath({});
  }, [selectedSchemaKey, isEditMode]);

  // Whenever required paths change, enforce them as ON in the included map
  useEffect(() => {
    if (!flatFields.length) return;
    setIncluded((prev) => {
      const copy = { ...prev };
      for (const p of requiredPaths) copy[p] = true;
      return copy;
    });
  }, [requiredPaths, flatFields.length]);

  /** ------------------------------------------------------------------ */
  /** Effects: schema switching                                             */
  /** ------------------------------------------------------------------ */

  /**
   * When schema changes (draft templates only), we:
   * - sanitize existing template data to fit the new schema
   * - rebuild included map based on the surviving data
   * - show a warning box if anything was removed/mismatched
   */
  useEffect(() => {
    if (!selectedSchema) return;
    if (isHydratingSchemaTree) return;

    const nextKey = `${selectedSchema.id}::${selectedSchema.version}`;
    if (hydratedSchemaKey !== nextKey) return;

    // First time schema loads (hydrate/initial select) -> no log
    if (!lastSchemaKey) {
      setLastSchemaKey(nextKey);
      setTpl((prev) => ({
        ...prev,
        schemaRef: { schemaId: selectedSchema.id, version: selectedSchema.version },
        updatedAt: nowUnixSeconds(),
      }));
      return;
    }

    // No real change
    if (lastSchemaKey === nextKey) return;

    // ✅ Schema changed: sanitize existing data to new schema
    const { data: nextData, log } = sanitizeTemplateDataToSchema(schemaBlocks, (tpl.data ?? {}) as JsonRecord);

    setTpl((prev) => ({
      ...prev,
      schemaRef: { schemaId: selectedSchema.id, version: selectedSchema.version },
      data: nextData,
      updatedAt: nowUnixSeconds(),
    }));

    // Rebuild included map based on surviving data (+ force required)
    const baseIncluded = buildIncludedFromData(nextData, null, {});
    for (const p of requiredPaths) baseIncluded[p] = true;
    setIncluded(baseIncluded);
    setSelectedPath(null);

    // Show log only if issues exist
    if (log.removedPaths.length || log.mismatchedPaths.length) {
      setSchemaSwitchLog(log);
    } else {
      setSchemaSwitchLog(null);
    }

    setLastSchemaKey(nextKey);
  }, [selectedSchema, schemaBlocks, tpl.data, lastSchemaKey, requiredPaths, isHydratingSchemaTree, hydratedSchemaKey]);

  /** ------------------------------------------------------------------ */
  /** UI actions: tree expand/collapse + selection                          */
  /** ------------------------------------------------------------------ */

  /**
   * Expand/collapse all object/array nodes.
   * Keeps leaves out of the open map.
   */
  const setSchemaAllOpen = (open: boolean) => {
    const next: Record<string, boolean> = {};
    for (const f of flatFields) {
      if (f.node.type === 'object' || f.node.type === 'array') {
        next[f.path] = open;
      }
    }
    setSchemaOpenByPath(next);
  };

  /**
   * Toggle one node open/closed.
   * Defaults: open in create mode, closed in edit mode.
   */
  const toggleSchemaOpen = (path: string) => {
    setSchemaOpenByPath((prev) => ({
      ...prev,
      [path]: !(prev[path] ?? !isEditMode),
    }));
  };

  const isSchemaOpen = (path: string) => schemaOpenByPath[path] ?? !isEditMode;

  /**
   * Clicking on `array.item.*` should select the array itself in the inspector.
   */
  const handleSelectPath = (path: string) => {
    const idx = path.indexOf('.item.');
    if (idx !== -1) {
      setSelectedPath(path.slice(0, idx));
      return;
    }
    setSelectedPath(path);
  };

  /** ------------------------------------------------------------------ */
  /** Template mutations: meta, toggles, values                             */
  /** ------------------------------------------------------------------ */

  /**
   * Update top-level template metadata (title, status, description).
   * Always updates `updatedAt`.
   */
  const updateTplMeta = (patch: Partial<PropertyTemplate>) => {
    if (readOnly) return;
    setTpl((prev) => ({ ...prev, ...patch, updatedAt: nowUnixSeconds() }));
  };

  /**
   * Toggle a schema subtree ON/OFF.
   * Rules:
   * - required nodes cannot be turned off
   * - toggling OFF removes all values in subtree from tpl.data
   * - toggling ON seeds default containers for object/array if missing
   */
  const handleToggle = (path: string, next: boolean) => {
    if (readOnly) return;

    // required nodes cannot be turned off (even if called directly)
    if (!next && requiredPaths.has(path)) return;

    const paths = collectDescendants(flatFields, path);

    // Update included map (force required ON)
    setIncluded((prev) => {
      const copy = { ...prev };
      for (const p of paths) {
        copy[p] = requiredPaths.has(p) ? true : next;
      }
      return copy;
    });

    // Toggle OFF: remove values for the subtree
    if (!next) {
      setTpl((prev) => {
        let data = prev.data;
        for (const p of paths) {
          data = unsetAtPath(data, p);
        }
        return { ...prev, data, updatedAt: nowUnixSeconds() };
      });
      return;
    }

    // Toggle ON: if node has no current value, seed a container for object/array
    const node = getNodeByPath(path);
    if (!node) return;

    setTpl((prev) => {
      const current = getAtPath(prev.data, path);
      if (current != null) return prev;

      if (node.type === 'object') {
        return {
          ...prev,
          data: setAtPath(prev.data, path, {}) as PropertyTemplate['data'],
          updatedAt: nowUnixSeconds(),
        };
      }

      if (node.type === 'array') {
        return {
          ...prev,
          data: setAtPath(prev.data, path, []) as PropertyTemplate['data'],
          updatedAt: nowUnixSeconds(),
        };
      }

      return prev;
    });
  };

  /**
   * Sets a value at an arbitrary schema path (used by array editor / object editor).
   * Automatically marks the path included.
   */
  const setValueForPath = (path: string, value: unknown) => {
    if (readOnly) return;

    setIncluded((prev) => ({ ...prev, [path]: true }));
    setTpl((prev) => ({
      ...prev,
      data: setAtPath(prev.data, path, value) as PropertyTemplate['data'],
      updatedAt: nowUnixSeconds(),
    }));
  };

  /**
   * Sets value for the currently selected inspector field.
   * Automatically marks the selected path included.
   */
  const setValueForSelected = (value: unknown) => {
    if (readOnly) return;
    if (!selectedField) return;
    const path = selectedField.path;

    setIncluded((prev) => ({ ...prev, [path]: true }));
    setTpl((prev) => ({
      ...prev,
      data: setAtPath(prev.data, path, value as JsonValue),
      updatedAt: nowUnixSeconds(),
    }));
  };

  /**
   * Clears the currently selected field value (does not change included toggle).
   */
  const clearSelected = () => {
    if (readOnly) return;
    if (!selectedField) return;
    const path = selectedField.path;
    setTpl((prev) => ({ ...prev, data: unsetAtPath(prev.data, path), updatedAt: nowUnixSeconds() }));
  };

  const applyImportedPayload = () => {
    if (readOnly) return;

    let importTargetOrgID = tpl.orgID;
    if (!isEditMode) {
      if (!orgID) {
        setImportError('Select an active organization before importing a template payload.');
        return;
      }
      importTargetOrgID = orgID;
    }

    if (!selectedSchema) {
      setImportError('Select a published schema from the dropdown before importing a template payload.');
      return;
    }

    const selectedKey = `${selectedSchema.id}::${selectedSchema.version}`;
    if (isHydratingSchemaTree || hydratedSchemaKey !== selectedKey) {
      setImportError('The selected schema is still loading. Try again in a moment.');
      return;
    }

    let parsed: unknown;
    try {
      parsed = JSON.parse(importPayload);
    } catch (error) {
      setImportError(error instanceof Error ? error.message : 'Payload must be valid JSON.');
      return;
    }

    const validation = validateTemplateImportPayload(parsed);
    if (!validation.ok) {
      setImportError(validation.errors.join('\n'));
      return;
    }

    const schemaLog = validateTemplateDataAgainstSchema({
      schemaBlocks,
      templateData: validation.template.data,
    });
    const sanitized = sanitizeTemplateDataToSchema(schemaBlocks, validation.template.data);
    const schemaErrors = [
      ...sanitized.log.removedPaths.map((path) => `${path}: not present in the selected schema.`),
      ...sanitized.log.mismatchedPaths.map((path) => `${path}: incompatible with the selected schema.`),
      ...formatTemplateValidationErrors(schemaLog),
    ];

    if (schemaErrors.length || hasSchemaValidationIssues(schemaLog)) {
      setImportError(`Template data does not match the selected schema:\n${[...new Set(schemaErrors)].join('\n')}`);
      return;
    }

    const nextIncluded = buildIncludedFromData(validation.template.data, null, {});
    for (const path of requiredPaths) nextIncluded[path] = true;

    setTpl((prev) => ({
      ...prev,
      orgID: isEditMode ? prev.orgID : importTargetOrgID,
      audience: validation.template.audience ?? prev.audience,
      schemaRef: { schemaId: selectedSchema.id, version: selectedSchema.version },
      title: `${validation.template.title.trim() || 'Template'}_cloned`,
      description: validation.template.description,
      data: validation.template.data,
      updatedAt: nowUnixSeconds(),
    }));
    setLastSchemaKey(selectedKey);
    setIncluded(nextIncluded);
    setSelectedPath(null);
    setSchemaSwitchLog(null);
    setTemplateValidationLog(null);
    setTemplateApiError(null);
    setDidTrySave(false);
    setImportError(null);
    setIsImportDialogOpen(false);
  };

  /** ------------------------------------------------------------------ */
  /** Preview helpers                                                      */
  /** ------------------------------------------------------------------ */

  const copyToClipboard = async () => {
    await navigator.clipboard.writeText(JSON.stringify(tpl, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  const deferredTpl = useDeferredValue(tpl);
  const previewJson = useMemo(() => JSON.stringify(deferredTpl, null, 2), [deferredTpl]);

  /** ------------------------------------------------------------------ */
  /** Validation: sanitize-based schema compatibility check                */
  /** ------------------------------------------------------------------ */

  /**
   * Validates the current template data against the currently selected schema.
   * Uses the same sanitizer used on schema switch:
   * - if incompatible fields exist, show warning box and optionally sanitize tpl.data
   */
  const validateBeforeSave = (): boolean => {
    if (tpl.schemaRef.schemaId && tpl.schemaRef.version && !selectedSchema) {
      toast.error('Selected schema could not be loaded. Refresh the page or choose another schema.');
      return false;
    }

    // no schema selected => nothing to validate
    if (!selectedSchema) return true;

    const log = validateTemplateDataAgainstSchema({
      schemaBlocks,
      templateData: tpl.data,
    });

    const hasIssues = log.missingRequired.length > 0 || log.typeMismatches.length > 0 || log.enumMismatches.length > 0;

    if (!hasIssues) {
      setTemplateValidationLog(null);
      return true;
    }

    // show validation errors (DO NOT sanitize/mutate here)
    setTemplateValidationLog(log);
    toast.error('Template does not match the selected schema. Fix the issues and try again.');
    return false;
  };

  /** ------------------------------------------------------------------ */
  /** Persistence: create/update template                                  */
  /** ------------------------------------------------------------------ */

  /**
   * Save draft template.
   * - First runs schema compatibility validation (sanitizer-based)
   * - Then runs basic checks (title, schemaRef)
   * - Finally dispatches create/update thunk
   */
  const onSaveDraft = async () => {
    if (!canShowSaveDraft) {
      toast.error(
        isEditMode
          ? 'You do not have permission to update templates.'
          : 'You do not have permission to create templates.',
      );
      return;
    }

    if (isEditMode && !hasUnsavedLocalChanges) {
      toast.error('No changes to save.');
      return;
    }

    setDidTrySave(true);

    if (!tpl.title.trim() || !tpl.description?.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!validateBeforeSave()) return;

    if (!tpl.title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!tpl.schemaRef.schemaId || !tpl.schemaRef.version) {
      toast.error('Schema reference missing');
      return;
    }
    if (!tpl.scope) {
      toast.error('Template scope is required');
      return;
    }
    const templateOrgID = isEditMode ? tpl.orgID : orgID;
    if (!templateOrgID) {
      toast.error('An active organization is required to save a template');
      return;
    }

    try {
      setTemplateApiError(null);

      if (isEditMode) {
        if (!tpl.id || tpl.id === 'TEMPLATE#new') {
          toast.error('Template id is missing');
          return;
        }

        await dispatch(
          updatePropertyTemplate({
            id: tpl.id,
            scope: tpl.scope,
            orgID: templateOrgID,
            audience: tpl.audience,
            status: tpl.status,
            title: tpl.title.trim(),
            description: tpl.description ?? '',
            schemaRef: tpl.schemaRef,
            data: tpl.data,
          }),
        ).unwrap();

        toast.success('Template updated');
        return;
      }

      const created = await dispatch(
        createPropertyTemplate({
          scope: tpl.scope,
          orgID: templateOrgID,
          status: tpl.status,
          audience: tpl.audience,
          schemaRef: tpl.schemaRef,
          title: tpl.title,
          description: tpl.description ?? '',
          data: tpl.data,
        }),
      ).unwrap();

      toast.success('Template created');

      const newId = (created.data as PropertyTemplate | undefined)?.id;
      if (!newId) throw new Error('Create succeeded but no schema id was returned from the API.');

      navigate(`/templates/${newId}/edit`);
    } catch (e) {
      const msg =
        typeof e === 'string' ? e : (e as Error)?.message ? String((e as Error).message) : 'Failed to save template';
      setTemplateApiError({
        title: isEditMode ? 'Failed to Save Template' : 'Failed to Create Template',
        message: msg,
      });
      toast.error(msg);
    }
  };

  const handleCloneTemplate = async () => {
    if (!storeTemplate || storeTemplate.status === 'deprecated') return;
    if (!canCreateTemplates) {
      toast.error('You do not have permission to create templates.');
      return;
    }
    if (isCloningTemplate) return;

    const ok = await confirm({
      title: 'Clone this template?',
      description: 'This will create a new draft copy you can edit.',
      confirmLabel: 'Clone',
    });
    if (!ok) return;

    setTemplateApiError(null);
    const promise = (async () => {
      const res = await dispatch(cloneTemplate({ id: storeTemplate.id })).unwrap();
      const newId = res.data?.id;
      if (!newId) {
        throw new Error('Clone succeeded but the API did not return the new template id.');
      }
      return { id: newId };
    })();

    toast.promise(promise, {
      loading: 'Cloning template…',
      success: 'Template cloned',
      error: (err) => err?.message || 'Failed to clone template',
    });

    try {
      const { id } = await promise;
      navigate(`/templates/${encodeURIComponent(id)}/edit`, { replace: true });
    } catch (error) {
      const message =
        typeof error === 'string'
          ? error
          : (error as Error)?.message
            ? String((error as Error).message)
            : 'Failed to clone template';
      setTemplateApiError({ title: 'Failed to Clone Template', message });
    }
  };

  const handleDiscardChanges = async () => {
    if (!storeTemplate || readOnly || !hasUnsavedLocalChanges) return;

    const ok = await confirm({
      title: 'Discard local changes?',
      description: 'This will reset the template to the last saved version.',
      confirmLabel: 'Discard changes',
      variant: 'destructive',
    });
    if (!ok) return;

    setTpl({
      id: storeTemplate.id,
      scope: storeTemplate.scope,
      orgID: storeTemplate.orgID,
      audience: storeTemplate.audience,
      status: storeTemplate.status,
      schemaRef: storeTemplate.schemaRef,
      title: storeTemplate.title ?? '',
      description: storeTemplate.description ?? '',
      data: (storeTemplate.data ?? {}) as JsonRecord,
      createdAt: storeTemplate.createdAt ?? nowUnixSeconds(),
      updatedAt: storeTemplate.updatedAt ?? nowUnixSeconds(),
      typeName: 'PropertyTemplate',
    });
    setSelectedSchemaKey(`${storeTemplate.schemaRef.schemaId}::${storeTemplate.schemaRef.version}`);
    setIncluded(buildIncludedFromData(storeTemplate.data ?? {}, null, {}));
    setSelectedPath(null);
    setSchemaSwitchLog(null);
    setTemplateValidationLog(null);
    setTemplateApiError(null);
    setSchemaApiError(null);
    setDidTrySave(false);
  };

  const handleDeprecateTemplate = async () => {
    if (!storeTemplate || storeTemplate.status !== 'published') return;
    if (!canUpdateTemplates) {
      toast.error('You do not have permission to update templates.');
      return;
    }
    if (isDeprecatingTemplate) return;

    const ok = await confirm({
      title: 'Deprecate this template?',
      description: 'This will prevent future use but keep it for historical records.',
      confirmLabel: 'Deprecate',
      variant: 'destructive',
    });
    if (!ok) return;

    setTemplateApiError(null);
    const promise = dispatch(deprecateTemplate({ id: storeTemplate.id })).unwrap();

    toast.promise(promise, {
      loading: 'Deprecating template…',
      success: 'Template deprecated',
      error: (err) => err?.message || 'Failed to deprecate template',
    });

    try {
      await promise;
      navigate('/templates', { replace: true });
    } catch (error) {
      const message =
        typeof error === 'string'
          ? error
          : (error as Error)?.message
            ? String((error as Error).message)
            : 'Failed to deprecate template';
      setTemplateApiError({ title: 'Failed to Deprecate Template', message });
    }
  };

  const handleRetryPublishedSchemas = async () => {
    try {
      await dispatch(fetchSchemas({ status: 'published' })).unwrap();
      toast.success('Published schemas refreshed');
    } catch (error) {
      const message =
        typeof error === 'string'
          ? error
          : (error as Error)?.message
            ? String((error as Error).message)
            : 'Failed to load published schemas';
      toast.error(message);
    }
  };

  /** ------------------------------------------------------------------ */
  /** Early returns: edit-mode loading / not found                          */
  /** ------------------------------------------------------------------ */

  if (isEditMode && !storeTemplate) {
    if (isLoadingTemplate || !hasAttemptedTemplateLoad) {
      return (
        <div className="space-y-6">
          {templateBreadcrumb}

          <PageHeader title={readOnly ? 'View Template' : 'Edit Template'} description="Loading template…" />

          <Card className="rounded-2xl">
            <CardHeader>
              <CardTitle>Loading…</CardTitle>
              <CardDescription>Looking for this template in the store.</CardDescription>
            </CardHeader>
            <CardContent className="flex justify-end">
              <Button type="button" variant="outline" disabled>
                Please wait
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }

    // fetched and still not found => not found
    return (
      <div className="space-y-6">
        {templateBreadcrumb}

        <PageHeader title={readOnly ? 'View Template' : 'Edit Template'} description="Template not found in store." />

        <Card className="rounded-2xl">
          <CardHeader>
            <CardTitle>Template not found</CardTitle>
            <CardDescription>
              We could not load this template from the server. It may have been deleted or you may not have access to
              it.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <ErrorNotice title={templateApiError?.title} message={templateApiError?.message} />

            <div className="flex justify-end">
              <Button type="button" variant="outline" onClick={() => navigate('/templates')}>
                Back to Templates
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  /** ------------------------------------------------------------------ */
  /** Render                                                              */
  /** ------------------------------------------------------------------ */

  return (
    <div className="space-y-6">
      {templateBreadcrumb}

      <PageHeader
        title={isEditMode ? (readOnly ? 'View Template' : 'Edit Template') : 'Template Builder'}
        description={
          isEditMode
            ? readOnly
              ? 'View an existing Property Template.'
              : 'Edit an existing Property Template.'
            : 'Build a Property Template from a published schema.'
        }
      />

      <ErrorNotice title={templateApiError?.title} message={templateApiError?.message} />
      <ErrorNotice title={schemaApiError?.title} message={schemaApiError?.message} />

      {storeTemplate && storeTemplate.status !== 'deprecated' && (readOnly || isEditMode) ? (
        <div className="space-y-2">
          <div className="flex items-center justify-end gap-2">
            {hasUnsavedLocalChanges ? (
              <Button type="button" variant="outline" onClick={handleDiscardChanges}>
                Discard changes
              </Button>
            ) : null}

            {canCreateTemplates ? (
              <Button
                type="button"
                variant="outline"
                onClick={handleCloneTemplate}
                disabled={isCloningTemplate || hasUnsavedLocalChanges}
              >
                Clone
              </Button>
            ) : null}

            {readOnly && storeTemplate.status === 'published' && canUpdateTemplates ? (
              <Button
                type="button"
                variant="destructive"
                onClick={handleDeprecateTemplate}
                disabled={isDeprecatingTemplate}
              >
                Deprecate
              </Button>
            ) : null}
          </div>

          {canCreateTemplates && hasUnsavedLocalChanges ? (
            <p className="text-muted-foreground text-right text-sm">Save or discard your changes before cloning.</p>
          ) : null}
        </div>
      ) : null}

      {!readOnly ? (
        <Card className="rounded-2xl">
          <CardHeader className="flex flex-row items-center justify-between gap-4">
            <div>
              <CardTitle>Create from Payload</CardTitle>
              <CardDescription>
                Select a schema, paste a template payload, then validate and hydrate this draft from its data.
              </CardDescription>
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setImportError(null);
                setIsImportDialogOpen(true);
              }}
            >
              Import Payload
            </Button>
          </CardHeader>
        </Card>
      ) : null}

      {/* Schema selection */}
      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle>{readOnly ? 'Schema' : 'Select Schema'}</CardTitle>
          <CardDescription>
            {readOnly ? 'Schema assigned to this template.' : 'Templates can only be created from published schemas.'}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-3">
          {readOnly ? (
            <div className="bg-muted/30 space-y-2 rounded-xl border p-3">
              <div className="text-sm font-medium">{selectedSchema?.title || 'Unknown schema'}</div>
              <div className="text-muted-foreground text-xs">
                Schema ID: {tpl.schemaRef.schemaId || '-'} · Version: {tpl.schemaRef.version || '-'}
              </div>
            </div>
          ) : (
            <>
              {isLoadingPublishedSchemas ? (
                <div className="bg-muted/30 flex items-center gap-3 rounded-xl border p-3 text-sm">
                  <LoadingSpinner size={20} />
                  <div>
                    <div className="font-medium">Loading published schemas…</div>
                    <div className="text-muted-foreground text-xs">Please wait before selecting a schema.</div>
                  </div>
                </div>
              ) : null}

              {publishedSchemasError ? (
                <div className="space-y-3">
                  <ErrorNotice title="Failed to Load Schemas" message={publishedSchemasError} />
                  <div className="flex justify-end">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => void handleRetryPublishedSchemas()}
                      disabled={isListingPublished}
                    >
                      {isListingPublished ? 'Retrying…' : 'Retry'}
                    </Button>
                  </div>
                </div>
              ) : null}

              <div className="space-y-2">
                <Label>Published schema</Label>

                <Select
                  value={selectedSchemaKey}
                  onValueChange={(v) => {
                    if (schemaLocked) {
                      toast.error('Schema is locked for published/deprecated templates.');
                      return;
                    }
                    setSelectedSchemaKey(v);
                  }}
                  disabled={isListingPublished || schemaLocked || Boolean(publishedSchemasError)}
                >
                  <SelectTrigger className="h-12">
                    <SelectValue placeholder={isListingPublished ? 'Loading…' : 'Select a schema'} />
                  </SelectTrigger>

                  <SelectContent className="z-[100]">
                    {schemaOptions.map((s) => (
                      <SelectItem key={`${s.id}::${s.version}`} value={`${s.id}::${s.version}`}>
                        {s.title} ({s.id} / {s.version})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {!isListingPublished && !publishedSchemasError && publishedSchemas.length === 0 ? (
                <div className="text-muted-foreground text-sm">No published schemas found. Publish a schema first.</div>
              ) : null}
            </>
          )}

          {schemaLocked && !isEditMode && !readOnly ? (
            <div className="flex justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setSelectedSchemaKey('');
                  setIncluded({});
                  setSelectedPath(null);
                  setTpl((prev) => ({
                    ...prev,
                    schemaRef: { schemaId: '', version: '' },
                    title: '',
                    description: '',
                    data: {},
                    updatedAt: nowUnixSeconds(),
                  }));
                  toast.success('Template cleared. You can select a new schema now.');
                }}
              >
                Reset template
              </Button>
            </div>
          ) : null}
        </CardContent>
      </Card>

      {/* Builder gated by schema selection */}
      {!selectedSchema ? (
        <Card className="rounded-2xl">
          <CardContent className="text-muted-foreground p-6 text-sm">
            {isLoadingPublishedSchemas && !selectedSchemaKey
              ? 'Loading published schemas…'
              : selectedSchemaKey
                ? isLoadingSelectedSchema
                  ? 'Loading selected schema…'
                  : 'Selected schema could not be loaded. Choose another schema or refresh the page.'
                : 'Select a schema above to start building a template.'}
          </CardContent>
        </Card>
      ) : (
        <>
          <PageHeader
            title="Template Details"
            description={`Selected: ${selectedSchema.title} (${selectedSchema.id} / ${selectedSchema.version})`}
          />

          {/* Schema compatibility warning box */}
          <SchemaIssuesAlert
            log={schemaSwitchLog ? issuesFromSchemaSwitchLog(schemaSwitchLog) : null}
            title="Lost or mismatched data"
            description="Your template was adjusted to match the new schema."
            onDismiss={() => setSchemaSwitchLog(null)}
          />

          <SchemaIssuesAlert
            log={templateValidationLog ? issuesFromSchemaValidationLog(templateValidationLog) : null}
            title="Template validation issues"
            description="Some fields are not compatible with the selected schema. Fix them before saving."
            onDismiss={() => setTemplateValidationLog(null)}
          />

          <div className="grid gap-6 lg:grid-cols-12">
            {/* Left: Schema tree */}
            <Card className="flex min-h-0 flex-col rounded-2xl lg:col-span-5">
              <CardHeader className="shrink-0">
                <CardTitle>Schema Fields</CardTitle>
                <CardDescription>Select which fields are included in this template.</CardDescription>

                <div className="flex gap-2 pt-2">
                  <Button type="button" variant="outline" size="sm" onClick={() => setSchemaAllOpen(true)}>
                    Expand all
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={() => setSchemaAllOpen(false)}>
                    Collapse all
                  </Button>
                </div>
              </CardHeader>

              <CardContent className="min-h-0 flex-1 space-y-3 overflow-auto">
                {isHydratingSchemaTree ? (
                  <div className="text-muted-foreground rounded-xl border p-4 text-sm">Preparing schema fields…</div>
                ) : schemaBlocks.length === 0 ? (
                  <div className="text-muted-foreground rounded-xl border p-4 text-sm">
                    This schema has no top-level attributes.
                  </div>
                ) : (
                  <SchemaFieldTree
                    nodes={schemaBlocks}
                    included={included}
                    onToggle={handleToggle}
                    selectedPath={selectedPath}
                    onSelect={handleSelectPath}
                    isOpen={isSchemaOpen}
                    onToggleOpen={toggleSchemaOpen}
                    disabled={readOnly}
                  />
                )}
              </CardContent>
            </Card>

            {/* Right: meta + inspector + preview */}
            <div className="space-y-6 lg:col-span-7">
              <TemplateMetaCard tpl={tpl} onChangeMeta={updateTplMeta} didTrySave={didTrySave} readOnly={readOnly} />

              <TemplateInspectorCard
                selectedField={selectedField}
                selectedValue={selectedValue}
                tplData={tpl.data as JsonRecord}
                onSetValueForSelected={setValueForSelected}
                onSetValueForPath={setValueForPath}
                onExcludeSelected={() => selectedField && handleToggle(selectedField.path, false)}
                onClearSelected={clearSelected}
                readOnly={readOnly}
              />

              <TemplatePreviewCard
                previewJson={previewJson}
                copied={copied}
                onCopy={copyToClipboard}
                onSave={onSaveDraft}
                isSaving={isSavingTemplate}
                canSave={canSaveDraft}
                showSave={canShowSaveDraft}
              />
            </div>
          </div>
        </>
      )}

      <Dialog open={isImportDialogOpen} onOpenChange={setIsImportDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Import Template Payload</DialogTitle>
            <DialogDescription>
              Paste a template payload. The pasted schema reference is ignored; validation uses the schema currently
              selected in the dropdown.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <ErrorNotice
              className="[&_p:last-child]:whitespace-pre-line"
              message={importError}
              title="Invalid Payload"
            />
            <Textarea
              value={importPayload}
              onChange={(event) => {
                setImportPayload(event.target.value);
                setImportError(null);
              }}
              rows={18}
              className="max-h-[60vh] min-h-[360px] font-mono text-xs"
              placeholder='{"title":"Builder Standard","description":"Imported draft","schemaRef":{"schemaId":"ignored","version":"ignored"},"data":{}}'
            />
            <p className="text-muted-foreground text-xs">
              Importing replaces the current draft data and appends <span className="font-mono">_cloned</span> to the
              pasted title. Schema id and version come from the selected dropdown schema.
            </p>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsImportDialogOpen(false);
                setImportError(null);
              }}
            >
              Cancel
            </Button>
            <Button type="button" onClick={applyImportedPayload} disabled={!importPayload.trim()}>
              Apply Payload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
